import { useState } from 'react';
import { Grid2 as Grid, Accordion, AccordionSummary, AccordionDetails, Typography, Button, Tooltip, IconButton } from "@mui/material";
import { ExpandMore, Delete, DragHandle, InfoOutlined } from "@mui/icons-material";
import { Droppable, Draggable } from '@hello-pangea/dnd';
import { CustomControlledInput } from "./CustomControlledInput";
import CommonCard from '../components/common_card';
import { TypographyBold } from "../style/typography";

const PartnerCompanyList = ({
    control,
    partnerCompanies,
    expandedPartnerCompany,
    facilities,
    handleEditClick,
    handleCancelEdit,
    handleDeleteClick,
    handleEditSubmit,
    openDialog,
    title,
    droppableId,
    isDisabled
}) => {
    const [tooltipOpen, setTooltipOpen] = useState(false);

    return (
        <Grid size={{ xs: 12 }}>
            <CommonCard
                child={
                    <Grid container spacing={3} sx={{ padding: 3 }}>
                        <Grid size={{ xs: 12 }}>
                            <TypographyBold sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Tooltip
                                    title="＝をドラッグ&ドロップで並べ替えができます"
                                    arrow
                                    open={tooltipOpen}
                                    onClose={() => setTooltipOpen(false)}
                                    PopperProps={{
                                        placement: 'bottom-end'
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setTooltipOpen(prev => !prev);
                                        }}
                                    >
                                        <InfoOutlined fontSize="medium" sx={{ cursor: "pointer", color: "#0A7CB7" }} />
                                    </IconButton>
                                </Tooltip>
                                {title}
                            </TypographyBold>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Droppable droppableId={droppableId}>
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef}>
                                        {Array.isArray(partnerCompanies) && partnerCompanies.map((partnerCompany, index) => (
                                            <Draggable key={partnerCompany.uuid} draggableId={String(partnerCompany.uuid)} index={index}>
                                                {(provided, snapshot) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        style={{ ...provided.draggableProps.style, marginBottom: '8px' }}
                                                    >
                                                        <Accordion
                                                            expanded={expandedPartnerCompany === partnerCompany.uuid}
                                                            onChange={() => {
                                                                if (expandedPartnerCompany === partnerCompany.uuid) {
                                                                    handleCancelEdit();
                                                                } else {
                                                                    handleEditClick(partnerCompany);
                                                                }
                                                            }}
                                                            sx={{ backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'inherit' }}
                                                        >
                                                            <AccordionSummary
                                                                expandIcon={<ExpandMore />}
                                                                aria-controls={`partner-company-content-${partnerCompany.uuid}`}
                                                                id={`partner-company-header-${partnerCompany.uuid}`}
                                                                sx={{ display: 'flex', alignItems: 'center' }}
                                                            >
                                                                <div {...provided.dragHandleProps} style={{ marginRight: '8px', cursor: 'grab', display: 'flex', alignItems: 'center' }}>
                                                                    <DragHandle color="action" />
                                                                </div>                                                                
                                                                <div>
                                                                    <Typography variant="h6">{partnerCompany.name}</Typography>
                                                                    <Typography variant="body2" color="text.secondary">
                                                                        担当:{" "}
                                                                        {partnerCompany.facilities?.length > 0
                                                                            ? partnerCompany.facilities
                                                                                .map((f) => f.name)
                                                                                .join(", ")
                                                                            : "未設定"}
                                                                    </Typography>
                                                                </div>
                                                            </AccordionSummary>
                                                            <AccordionDetails>
                                                                <Grid container spacing={2} sx={{ paddingTop: 3 }}>
                                                                    {/* 連絡先会社名 */}
                                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                                        <CustomControlledInput
                                                                            name="editingPartnerCompany.name"
                                                                            control={control}
                                                                            label="連絡先会社名"
                                                                            variant="outlined"
                                                                            inputProps={{ maxLength: 100 }}
                                                                            disabled={isDisabled}
                                                                        />
                                                                    </Grid>
                                                                    {/* 担当発電所 */}
                                                                    <Grid size={{ xs: 12, md: 6 }}>
                                                                        <CustomControlledInput
                                                                            name="editingPartnerCompany.facilities"
                                                                            control={control}
                                                                            label="担当発電所"
                                                                            type="select"
                                                                            multiple
                                                                            options={facilities.map(p => ({
                                                                                value: p.uuid,
                                                                                label: p.name
                                                                            }))}
                                                                            disabled={isDisabled}
                                                                        />
                                                                    </Grid>
                                                                    {/* ボタン群 */}
                                                                    <Grid
                                                                        size={{ xs: 12 }}
                                                                        display="flex"
                                                                        justifyContent="space-between"
                                                                        gap={2}
                                                                        sx={{ mt: 2 }}
                                                                    >
                                                                        {/* 削除ボタン */}
                                                                        <Button
                                                                            variant="outlined"
                                                                            color="error"
                                                                            onClick={() => handleDeleteClick(partnerCompany)}
                                                                            startIcon={<Delete />}
                                                                            disabled={isDisabled}
                                                                        >
                                                                            削除
                                                                        </Button>
                                                                        <div style={{ display: "flex", gap: "8px" }}>
                                                                            {/* キャンセルボタン */}
                                                                            <Button variant="outlined" onClick={handleCancelEdit} disabled={isDisabled}>
                                                                                キャンセル
                                                                            </Button>
                                                                            <Button
                                                                                variant="contained"
                                                                                onClick={handleEditSubmit(
                                                                                    (data) => { openDialog('save', data.editingPartnerCompany); }
                                                                                )}
                                                                                sx={{
                                                                                    backgroundColor: 'rgba(124, 183, 10, 1.0)',
                                                                                    '&:hover': { backgroundColor: 'rgba(124, 183, 10, 0.8)' }
                                                                                }}
                                                                                 disabled={isDisabled}
                                                                            >
                                                                                保存
                                                                            </Button>
                                                                        </div>
                                                                    </Grid>
                                                                </Grid>
                                                            </AccordionDetails>
                                                        </Accordion>
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </Grid>
                    </Grid>
                }
            />
        </Grid>
    );
};

export default PartnerCompanyList;